import { BadgeCategory } from '../../models/Badge';
import { Gender } from '../../models/User';
import {
    formatProgressionAmount,
    getNextProgressionBadgeForCategory,
    resolveBadgeTitle,
} from '../../utils/badgeCatalog';
import { applyGender } from '../../utils/genderedText';

/**
 * Formatters for the streak indicator's tooltip / mobile modal. Pure
 * functions so the copy is testable without a Vue harness, mirroring the
 * `badgeDisplayLines` module that drives the badge chips.
 *
 * The "next-tier" line is anchored on `longestStreak` because that's what
 * actually unlocks badges — current_streak is the live counter. So the
 * indicator tells you both how far you are right now (currentStreak) and
 * what your current streak would need to reach to unlock the next badge.
 */

export type StreakLines = {
    /** Headline count, e.g. "3 uzastopna eventa" or "0 uzastopnih evenata". */
    currentLine: string;
    /**
     * Either "Još N eventa do značke <title>" or the top-of-ladder message.
     * Always returns a string — there is always something to say.
     */
    nextLine: string;
    /**
     * Optional personal-best line shown when longestStreak strictly exceeds
     * currentStreak (i.e. the user previously hit a higher streak and broke
     * it). Null otherwise — we don't repeat the same number twice.
     */
    personalBestLine: string | null;
};

const TOP_OF_LADDER_TEMPLATE = 'Postig(ao|la) si najvišu razinu niza! 🔥';

export function streakLines(
    currentStreak: number,
    longestStreak: number,
    gender: Gender = Gender.Male,
): StreakLines {
    const currentLine = formatProgressionAmount(BadgeCategory.Streak, currentStreak)
        ?? `${currentStreak}`;

    const nextLine = nextStreakLine(currentStreak, longestStreak, gender);
    const personalBestLine = personalBestStreakLine(currentStreak, longestStreak);

    return { currentLine, nextLine, personalBestLine };
}

function nextStreakLine(currentStreak: number, longestStreak: number, gender: Gender): string {
    // The next unlockable badge is the smallest threshold above the user's
    // historical best — current can only unlock it by growing past longest.
    const nextBadge = getNextProgressionBadgeForCategory(
        BadgeCategory.Streak,
        longestStreak,
    );

    if (nextBadge == null) {
        return applyGender(TOP_OF_LADDER_TEMPLATE, gender);
    }

    // To unlock the next tier the current streak must grow to nextBadge.threshold.
    // If current already exceeds the threshold (transient window before the
    // next recompute writes the badge), pretend at least 1 event remains so
    // the line doesn't say "0 more".
    const remaining = Math.max(1, nextBadge.threshold - currentStreak);
    const amount = formatProgressionAmount(BadgeCategory.Streak, remaining);
    const nextTitle = resolveBadgeTitle(nextBadge, gender);

    return amount
        ? `Još ${amount} do značke ${nextTitle}`
        : `Sljedeća značka: ${nextTitle}`;
}

function personalBestStreakLine(currentStreak: number, longestStreak: number): string | null {
    if (longestStreak <= currentStreak) {
        return null;
    }

    const amount = formatProgressionAmount(BadgeCategory.Streak, longestStreak);
    return amount
        ? `Najduži dosad: ${amount}`
        : null;
}
